/* eslint-disable @typescript-eslint/no-non-null-assertion */

import * as React from 'react';
import { Button, ModalWindow, UIHeading2, UIHeading4, UIText1, UIText2 } from '@cian/ui-kit';

import { IGetVillageListRequestSchema } from '../../../../node/repositories/countryside-search/entities/schemas/GetVillageListRequestSchema';
import { IVillageCounterSetSchema } from '../../../../node/repositories/countryside-search/entities/counter/VillageCounterSetSchema';
import { INewobjectCategorySetForListPresenterSchema } from '../../../../node/repositories/countryside-search/entities/newobject_category/NewobjectCategorySetForListPresenterSchema';
import { IVillageForListPresenterSchema } from '../../../../node/repositories/countryside-search/entities/village/VillageForListPresenterSchema';

import { MODERATION_STATUS_TYPES, NEW_OBJECTS_SET_KEYS, ON_SALE_OBJECTS } from '../../../interfaces/villages';
import { getVillageOffersType } from '../../../utils/getVillageOffersType';
import { formatPhone, getMobilePriceRange, getPromotionLabels } from '../../../utils';
import { PriceDisclaimer } from '../../PriceDisclaimer';
import { getCounterSetOnSale, getHighway, getVillageInfo } from '../../village_list_item/helpers';
import { trackCardOpened, trackListingObjectsOpened } from '../../village_list_item/tracking';
import { trackPriceOpened } from '../../village_object/tracking';
import { MobileListItemObject } from './object';
import { GalleryContainer } from '../../../containers/GalleryContainer';
import * as styles from './index.css';
import { LabelsList } from '../../LabelsList';

export interface IMobileListItemProps {
  village: IVillageForListPresenterSchema;
  filters: IGetVillageListRequestSchema;
  regionHasRoads: boolean;
  trackPhoneOpened(village: IVillageForListPresenterSchema): void;
}

interface IMobileListItemState {
  contactsPopupVisible: boolean;
  galleryControlsHidden: boolean;
}

const getOnSale = (
  counterSet: IVillageCounterSetSchema,
  objectType: keyof INewobjectCategorySetForListPresenterSchema,
): number => {
  return counterSet[ON_SALE_OBJECTS[objectType]] || 0;
};

export class MobileListItem extends React.Component<IMobileListItemProps, IMobileListItemState> {
  public state = {
    contactsPopupVisible: false,
    galleryControlsHidden: false,
  };

  private hadShownPhone = false;

  public render() {
    const { village, filters, regionHasRoads } = this.props;
    const {
      builder: { phone, displayPhone, moderationStatus, name: builderName },
      name,
      seo,
      newobjectCategorySet,
      offersSeo,
      counterSet,
      thumbnailList,
      videoList,
      builderCounterSet,
      resaleCounterSet,
      isCropped,
      promotions,
    } = village;
    const showBuilderContacts = !!phone && displayPhone && moderationStatus === MODERATION_STATUS_TYPES.Published;
    const offersLink = offersSeo && offersSeo.url;
    const offersCount = getCounterSetOnSale(counterSet!);
    const highway = getHighway(regionHasRoads, filters, village, true);
    const address = this.addressDetails.map(({ fullName }) => fullName).join(', ');
    const priceRange = getMobilePriceRange(village);
    const disclaimerType = getVillageOffersType({
      builderCounterSet,
      resaleCounterSet,
    });
    const labels = getPromotionLabels(promotions);

    return (
      <div className={styles['container']}>
        <div className={styles['gallery_container']} onClick={this.handleGalleryClick}>
          <GalleryContainer images={thumbnailList} videos={videoList} deviceType="mobile" />
        </div>
        <div className={styles['card']}>
          <div className={styles['card-content']}>
            <a className={styles['card-link']} href={(seo && seo.url) || ''} onClick={this.handleVillageClick} />
            <div className={styles['card-info']}>
              {labels && (
                <div className={styles['labels']}>
                  <LabelsList labels={labels} />
                </div>
              )}
              <UIHeading2 data-name="CardVillageName">{name}</UIHeading2>
              <div className={styles['info']}>
                <UIText2 color="gray60_100">{getVillageInfo(village)}</UIText2>
              </div>
              {!isCropped && priceRange && (
                <div className={styles['price']}>
                  <UIHeading4>{priceRange}</UIHeading4>
                  {!!disclaimerType && <PriceDisclaimer mediaType="mobile" type={disclaimerType} />}
                </div>
              )}
              {highway && <div className={styles['highway']}>{highway}</div>}
              {address && (
                <div className={styles['address']}>
                  <UIText2 color="gray60_100">{address}</UIText2>
                </div>
              )}
            </div>
          </div>
          {!!(offersLink && offersCount) && (
            <a className={styles['offers']} href={offersLink} onClick={this.onClickLink(offersLink)}>
              <UIText1 color="primary_100">Все объекты в этом посёлке</UIText1>
              <UIText1 color="primary_100">{getCounterSetOnSale(counterSet!)}</UIText1>
            </a>
          )}
          {showBuilderContacts && (
            <div className={styles['contacts']}>
              <Button
                fullWidth
                onClick={this.handleShowContactsClick}
                data-name="CardPhoneButton"
                data-testid="CardPhoneButton"
              >
                Показать телефон
              </Button>
              <ModalWindow
                open={this.state.contactsPopupVisible}
                onClose={this.togglePopup}
                content={
                  <>
                    <div className={styles['contacts-builder']}>
                      <UIText1>{builderName}</UIText1>
                    </div>
                    <UIHeading2>{formatPhone(phone)}</UIHeading2>
                  </>
                }
              />
            </div>
          )}
        </div>
        <div className={styles['objects']}>
          {Object.values(NEW_OBJECTS_SET_KEYS).map(key => (
            <MobileListItemObject
              key={key}
              object={newobjectCategorySet && newobjectCategorySet[key]}
              objectType={key}
              onSale={getOnSale(counterSet!, key)}
              onClickLink={this.onClickLink}
              hidePrice={!!isCropped}
            />
          ))}
        </div>
      </div>
    );
  }

  private togglePopup = () => this.setState({ contactsPopupVisible: !this.state.contactsPopupVisible });

  private handleVillageClick = () => trackCardOpened(this.props.village);

  private handleGalleryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const {
      village: { seo },
    } = this.props;
    if (seo) {
      window.location.href = seo.url as string;
    }
    this.handleVillageClick();
  };

  private handleShowContactsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!this.hadShownPhone) {
      this.props.trackPhoneOpened(this.props.village);
      this.hadShownPhone = true;
    }
    this.togglePopup();
  };

  private onClickLink = (offersLink: string, objectType?: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const { village } = this.props;
    if (objectType) {
      trackPriceOpened(objectType, village.id);
    } else {
      trackListingObjectsOpened(village);
    }
    location.href = offersLink;
  };

  private get addressDetails() {
    const details = this.props.village.geo?.address?.details || [];

    return details.filter(({ type }) => type === 'location');
  }
}
